import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';

import { AuthService } from './services/auth.service';
import { ActAsService } from './services/act-as.service';
import { NAV_TIERS, ORG_MIN_RANK } from './layout/default-layout/_nav';
import { OrgPage, orgPageOf, orgUrl, slugOf } from './org-url';

/** The first real role route guard: deep links stop rendering pages the
 *  API would 403. Reads the same NAV_TIERS map as the sidebar filter so
 *  the two can never disagree. Cosmetic-plus, as ever: the backend
 *  gates are the real ones.
 *
 *  Org staff live inside their wall, and that wall has an address: every
 *  page they can reach is `/o/<their slug>/…`, plus their own profile.
 *  Platform staff enter the org console only as a super admin acting-as
 *  an org, on that org's base.
 */
export const tierGuard: CanActivateChildFn = (route, state) => {
  const auth = inject(AuthService);
  const actAs = inject(ActAsService);
  const router = inject(Router);

  const url = state.url.replace(/^\//, '').split('?')[0];
  const first = url.split('/')[0];
  const page = orgPageOf(url);
  const context = auth.orgContext();

  if (context) {
    // Their own base, their own slug. A foreign or misspelled slug is a
    // typo, not an attempt: rewrite it onto their own rather than 404.
    const to = (p: OrgPage) => orgUrl(context.slug, p);
    if (first === 'profile') return true;
    if (page === null) return router.createUrlTree([to('dashboard')]);
    if (rankRefuses(page, context.orgRole)) {
      return router.createUrlTree([to('dashboard')]);
    }
    return slugOf(url) === context.slug
      ? true
      : router.createUrlTree([to(page)]);
  }

  if (first === 'o' || first === 'org') {
    // A platform account on an org URL: only a super admin, only while
    // acting-as, and only on that org's own base.
    const acting = actAs.org();
    if (!auth.isSuperAdmin() || !acting) {
      return router.createUrlTree([
        auth.isSuperAdmin() ? '/organizations' : '/dashboard'
      ]);
    }
    if (page === null) return router.createUrlTree([orgUrl(acting.slug)]);
    return slugOf(url) === acting.slug
      ? true
      : router.createUrlTree([orgUrl(acting.slug, page)]);
  }

  const tier = NAV_TIERS[url] ?? NAV_TIERS[first] ?? 'all';
  const ok =
    tier === 'super' ? auth.isSuperAdmin()
    : tier === 'admin' ? auth.isAdmin()
    : tier === 'marketing' ? auth.isMarketing()
    : tier === 'support' ? auth.isSupport()
    : true;
  return ok ? true : router.createUrlTree(['/dashboard']);
};

/** Org rank outranks by being SMALLER (OWNER=1, ADMIN=2, SUPPORT=3), the
 *  same comparison `require_org_scope` makes on the backend. */
function rankRefuses(page: OrgPage, orgRole: number): boolean {
  const min = ORG_MIN_RANK[page];
  return min !== undefined && orgRole > min;
}
